import {
  ICreateNodeRequest,
  IMoveNodesRequest,
  IUpdateNodeRequest,
  IDeleteNodeRequest,
  ISplitRowRequest,
  IMoveCellRequest,
  ICreateRowRequest,
  IUpdateCellsRequest,
  IMoveRowRequest,
  IDeleteRowRequest,
  IContentRequest,
  UpdateRowRequest,
} from '@/editor/api/services/types/requests';
import { INode, ICreateImage, IStoredImage, IEditorBlock, IRow } from '@/editor/types';
import {
  createNodeMutation,
  createRowMutation,
  moveNodesMutation,
  moveCellMutation,
  moveRowMutation,
  updateCellsMutation,
  updateNodeMutation,
  deleteNodeMutation,
  deleteRowMutation,
  uploadImageMutation,
  splitRowMutation,
  createEditorBlockMutation,
  copyEditorBlockMutation,
  updateRowMutation,
} from '@/editor/api/graphql/editor-block.mutations';
import { editorBlockQuery } from '@/editor/api/graphql/editor-block.queries';
import { apolloClient } from '@/apollo';
import { EContentRequestType } from '@/editor/editor-block/store/editor-ui.types';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';

export class EditorBlockService {
  static async loadEditorBlock(id: string): Promise<IEditorBlock> {
    const {
      data: { editorBlock },
    } = await apolloClient.query({
      query: editorBlockQuery,
      variables: { id },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return editorBlock;
  }

  static async copyEditorBlock(id: string): Promise<IEditorBlock> {
    const {
      data: { copyEditorBlock },
    } = await apolloClient.mutate({
      mutation: copyEditorBlockMutation,
      variables: { id },
    });
    return copyEditorBlock;
  }

  static async createEditorBlock(): Promise<IEditorBlock> {
    const {
      data: { createEditorBlock },
    } = await apolloClient.mutate({
      mutation: createEditorBlockMutation,
    });
    return createEditorBlock;
  }

  static async createNode(payload: ICreateNodeRequest): Promise<INode> {
    const {
      data: { newNode },
    } = await apolloClient.mutate({
      mutation: createNodeMutation,
      variables: payload,
    });
    return newNode;
  }

  static async createRow(payload: ICreateRowRequest): Promise<IRow> {
    const {
      data: { newRow },
    } = await apolloClient.mutate({
      mutation: createRowMutation,
      variables: payload,
    });
    return newRow;
  }

  static async moveNodes(payload: IMoveNodesRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: moveNodesMutation,
      variables: payload,
    });
    return !!result;
  }

  static async moveCell(payload: IMoveCellRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: moveCellMutation,
      variables: payload,
    });
    return !!result;
  }

  static async moveRow(payload: IMoveRowRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: moveRowMutation,
      variables: payload,
    });
    return !!result;
  }

  static async updateCells(payload: IUpdateCellsRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: updateCellsMutation,
      variables: payload,
    });
    return !!result;
  }

  static async updateNode(payload: IUpdateNodeRequest): Promise<INode> {
    const {
      data: { updatedNode },
    } = await apolloClient.mutate({
      mutation: updateNodeMutation,
      variables: payload,
    });
    return updatedNode;
  }

  static async splitRow(payload: ISplitRowRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: splitRowMutation,
      variables: payload,
    });
    return !!result;
  }

  static async deleteNode(payload: IDeleteNodeRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: deleteNodeMutation,
      variables: payload,
    });
    return !!result;
  }

  static async deleteRow(payload: IDeleteRowRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: deleteRowMutation,
      variables: payload,
    });
    return !!result;
  }

  static async updateRow(payload: UpdateRowRequest): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: updateRowMutation,
      variables: payload,
    });
    return !!result;
  }

  static async createImage({ name, url, type, file }: ICreateImage): Promise<IStoredImage> {
    const {
      data: { createImage },
    } = await apolloClient.mutate({
      mutation: uploadImageMutation,
      variables: {
        name,
        url,
        type,
        file,
      },
    });
    return createImage;
  }

  static async dispatchRequest(payload: IContentRequest, type: EContentRequestType) {
    switch (type) {
      case EContentRequestType.UpdateCells: {
        await this.updateCells(payload as IUpdateCellsRequest);
        break;
      }
      case EContentRequestType.CreateRow: {
        await this.createRow(payload as ICreateRowRequest);
        break;
      }
      case EContentRequestType.SplitTextCreateRow: {
        await this.splitRow(payload as ISplitRowRequest);
        break;
      }
      case EContentRequestType.DeleteRow: {
        await this.deleteRow(payload as IDeleteRowRequest);
        break;
      }
      case EContentRequestType.MoveRow: {
        await this.moveRow(payload as IMoveRowRequest);
        break;
      }
      case EContentRequestType.MoveNodes: {
        await this.moveNodes(payload as IMoveNodesRequest);
        break;
      }
      case EContentRequestType.MoveCell: {
        await this.moveCell(payload as IMoveNodesRequest);
        break;
      }
      case EContentRequestType.UpdateNode: {
        await this.updateNode(payload as IUpdateNodeRequest);
        break;
      }
      case EContentRequestType.DeleteNode: {
        await this.deleteNode(payload as IUpdateNodeRequest);
        break;
      }
      case EContentRequestType.UpdateRow: {
        await this.updateRow(payload as UpdateRowRequest);
        break;
      }
      default:
        break;
    }
  }
}
